import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { getCities, getDistricts, getNeighborhoods } from '../utils/staticLocationData';
import { getHybridCities, getHybridDistricts, getHybridNeighborhoods } from '../utils/tradresAPI';
import { 
  MapPinIcon, 
  UserIcon, 
  PhoneIcon, 
  BuildingOfficeIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

function ModernAddressForm({ addressToEdit, onFormSubmit, closeModal, defaultAddressType = 'delivery' }) {
    const { token } = useAuth();
    const [formData, setFormData] = useState({
        address_title: '',
        full_name: '',
        phone: '',
        country: 'Türkiye',
        city: '',
        district: '',
        neighborhood: '',
        address_line: '',
        address_type: defaultAddressType
    });
    const [provinces, setProvinces] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [neighborhoods, setNeighborhoods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingProvinces, setLoadingProvinces] = useState(true);
    const [loadingDistricts, setLoadingDistricts] = useState(false);
    const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);

    useEffect(() => {
        const getProvinces = async () => {
            setLoadingProvinces(true);
            try {
                // Hızlı Tradres API ile hibrit yaklaşım
                const provincesData = await getHybridCities(getCities);
                
                // Şehirleri özel sıralama ile düzenle: İstanbul, Ankara, İzmir önce
                const sortedProvinces = sortCitiesByPriority(provincesData);
                setProvinces(sortedProvinces);
                
                // Eğer yeni adres ekliyorsa (düzenleme değilse) default İstanbul seç
                if (!addressToEdit) {
                    await setDefaultIstanbul(sortedProvinces);
                }
                
                console.log('✅ Adres formu: Şehirler yüklendi');
            } catch (error) {
                console.error('İller yüklenirken hata:', error);
                toast.error('İller yüklenirken hata oluştu');
                // Son çare olarak statik veriyi dene
                try {
                    const staticCities = await getCities();
                    const sortedCities = sortCitiesByPriority(staticCities);
                    setProvinces(sortedCities);
                    if (!addressToEdit) {
                        await setDefaultIstanbul(sortedCities);
                    }
                } catch (staticError) {
                    console.error('Statik şehirler de yüklenemedi:', staticError);
                }
            } finally {
                setLoadingProvinces(false);
            }
        };
        getProvinces();
    }, []);

    useEffect(() => {
        if (addressToEdit) {
            setFormData({
                address_title: addressToEdit.address_title || '',
                full_name: addressToEdit.full_name || '',
                phone: addressToEdit.phone || '',
                country: addressToEdit.country || 'Türkiye',
                city: addressToEdit.city || '',
                district: addressToEdit.district || '',
                neighborhood: addressToEdit.neighborhood || '',
                address_line: addressToEdit.address_line || '',
                address_type: addressToEdit.address_type || defaultAddressType
            });
        } else {
            setFormData(prev => ({ ...prev, address_type: defaultAddressType }));
        }
    }, [addressToEdit, defaultAddressType]);

    // Adres düzenlenirken ilçe ve mahalle verilerini otomatik yükle
    useEffect(() => {
        const loadEditAddressData = async () => {
            if (addressToEdit && addressToEdit.city && provinces.length > 0) {
                console.log('🔄 Adres düzenleme: İlçe ve mahalle verileri yükleniyor...', addressToEdit.city);
                
                // İlçeleri yükle
                if (addressToEdit.city) {
                    try {
                        setLoadingDistricts(true);
                        const districtsData = await getHybridDistricts(addressToEdit.city, getDistricts, null);
                        setDistricts(districtsData);
                        console.log('✅ Adres düzenleme: İlçeler yüklendi:', districtsData.length, 'adet');
                        
                        // Mahalleleri yükle (eğer ilçe bilgisi varsa)
                        if (addressToEdit.district) {
                            setLoadingNeighborhoods(true);
                            const neighborhoodsData = await getHybridNeighborhoods(addressToEdit.city, addressToEdit.district, getNeighborhoods, null);
                            setNeighborhoods(neighborhoodsData);
                            console.log('✅ Adres düzenleme: Mahalleler yüklendi:', neighborhoodsData.length, 'adet');
                        }
                    } catch (error) {
                        console.error('❌ Adres düzenleme veri yükleme hatası:', error);
                    } finally {
                        setLoadingDistricts(false);
                        setLoadingNeighborhoods(false);
                    }
                }
            }
        };
        
        loadEditAddressData();
    }, [addressToEdit, provinces]); // provinces yüklendiğinde ve addressToEdit değiştiğinde çalışır

    const handleProvinceChange = async (e) => {
        const provinceName = e.target.value;
        setFormData(prev => ({ ...prev, city: provinceName, district: '', neighborhood: '' }));
        setDistricts([]);
        setNeighborhoods([]);
        
        if (provinceName) {
            setLoadingDistricts(true);
            try {
                console.log(`🔄 Adres formu: ${provinceName} ilçeleri yükleniyor...`);
                // Doğrudan Tradres API'yi kullan
                const districtsData = await getHybridDistricts(provinceName, getDistricts, null);
                setDistricts(districtsData);
                console.log(`✅ Adres formu: ${provinceName} ilçeleri yüklendi (${districtsData.length} adet)`);
            } catch (error) {
                console.error('İlçeler yüklenirken hata:', error);
                toast.error('İlçeler yüklenirken hata oluştu');
                setDistricts([]);
            } finally {
                setLoadingDistricts(false);
            }
        }
    };

    const handleDistrictChange = async (e) => {
        const districtName = e.target.value;
        setFormData(prev => ({ ...prev, district: districtName, neighborhood: '' }));
        setNeighborhoods([]);
        
        if (districtName) {
            setLoadingNeighborhoods(true);
            try {
                console.log(`🔄 Adres formu: ${formData.city}/${districtName} mahalleleri yükleniyor...`);
                // Doğrudan Tradres API'yi kullan
                const cityName = formData.city;
                const neighborhoodsData = await getHybridNeighborhoods(cityName, districtName, getNeighborhoods, null);
                setNeighborhoods(neighborhoodsData);
                console.log(`✅ Adres formu: ${districtName} mahalleleri yüklendi (${neighborhoodsData.length} adet)`);
            } catch (error) {
                console.error('Mahalleler yüklenirken hata:', error);
                toast.error('Mahalleler yüklenirken hata oluştu');
                setNeighborhoods([]);
            } finally {
                setLoadingNeighborhoods(false);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Şehirleri öncelik sırasına göre sırala: İstanbul, Ankara, İzmir önce
    const sortCitiesByPriority = (cities) => {
        const priorityCities = ['İSTANBUL', 'ANKARA', 'İZMİR'];
        const priority = [];
        const others = [];
        
        cities.forEach(city => {
            const cityName = city.name.toUpperCase();
            const priorityIndex = priorityCities.indexOf(cityName);
            if (priorityIndex !== -1) {
                priority[priorityIndex] = city;
            } else {
                others.push(city);
            }
        });
        
        // Boş slotları filtrele ve diğerlerini alfabetik sırala
        const sortedPriority = priority.filter(Boolean);
        const sortedOthers = others.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        
        return [...sortedPriority, ...sortedOthers];
    };

    // Default olarak İstanbul'u seç ve ilçelerini yükle (sadece yeni adres için)
    const setDefaultIstanbul = async (cities) => {
        const istanbul = cities.find(city => city.name.toUpperCase() === 'İSTANBUL');
        if (istanbul && (!formData.city || formData.city === '')) {
            console.log('🏙️ Adres formu: Default olarak İstanbul seçiliyor...');
            
            // İstanbul'u seç
            setFormData(prev => ({ 
                ...prev, 
                city: istanbul.name,
                district: '',
                neighborhood: ''
            }));
            
            try {
                // İstanbul'un ilçelerini yükle
                const districtsData = await getHybridDistricts(istanbul.name, getDistricts, null);
                setDistricts(districtsData);
                console.log(`✅ Adres formu: İstanbul ilçeleri yüklendi (${districtsData.length} adet)`);
                
                // İlk ilçeyi seç (Kadıköy varsa onu, yoksa ilk ilçeyi)
                const kadikoy = districtsData.find(d => d.name.toUpperCase().includes('KADIKOY'));
                const firstDistrict = kadikoy || districtsData[0];
                
                if (firstDistrict) {
                    setFormData(prev => ({ 
                        ...prev, 
                        district: firstDistrict.name,
                        neighborhood: ''
                    }));
                    
                    try {
                        // İlk ilçenin mahallelerini yükle
                        const neighborhoodsData = await getHybridNeighborhoods(istanbul.name, firstDistrict.name, getNeighborhoods, null);
                        setNeighborhoods(neighborhoodsData);
                        console.log(`✅ Adres formu: ${firstDistrict.name} mahalleleri yüklendi (${neighborhoodsData.length} adet)`);
                    } catch (error) {
                        console.warn('⚠️ Adres formu: Default mahalle yüklenirken sorun:', error.message);
                    }
                }
            } catch (error) {
                console.warn('⚠️ Adres formu: Default ilçe yüklenirken sorun:', error.message);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const url = addressToEdit ? `/api/addresses/${addressToEdit.id}` : '/api/addresses';
        const method = addressToEdit ? 'PUT' : 'POST';

        try {
            console.log('💾 Adres kaydediliyor:', formData);
            await api(url, {
                method,
                headers: { 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(formData)
            });
            toast.success(`Adres başarıyla ${addressToEdit ? 'güncellendi' : 'eklendi'}.`);
            onFormSubmit();
            closeModal();
        } catch (err) {
            console.error('❌ Adres kaydetme hatası:', err);
            toast.error(err.message || 'Adres kaydedilirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-xl w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-brand-green to-brand-green-light p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="bg-white/20 p-2 rounded-xl">
                            <MapPinIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-white">
                                {addressToEdit ? 'Adresi Düzenle' : 'Yeni Adres Ekle'}
                            </h3>
                            <p className="text-white/80 text-sm">
                                {formData.address_type === 'delivery' ? 'Teslimat Adresi' : 'Fatura Adresi'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={closeModal}
                        className="bg-white/20 hover:bg-white/30 p-2 rounded-xl transition-colors"
                    >
                        <XMarkIcon className="w-5 h-5 text-white" />
                    </button>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Adres Türü */}
                <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        Adres Türü
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                        <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, address_type: 'delivery' }))}
                            className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                                formData.address_type === 'delivery'
                                    ? 'border-brand-green bg-brand-green/10 text-brand-green'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <div className="flex items-center justify-center space-x-2">
                                <MapPinIcon className="w-5 h-5" />
                                <span className="font-medium">Teslimat Adresi</span>
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, address_type: 'billing' }))}
                            className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                                formData.address_type === 'billing'
                                    ? 'border-brand-green bg-brand-green/10 text-brand-green'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <div className="flex items-center justify-center space-x-2">
                                <BuildingOfficeIcon className="w-5 h-5" />
                                <span className="font-medium">Fatura Adresi</span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Kişisel Bilgiler */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <UserIcon className="w-4 h-4 inline mr-1" />
                            Adres Başlığı *
                        </label>
                        <input
                            type="text"
                            name="address_title"
                            value={formData.address_title}
                            onChange={handleInputChange}
                            placeholder="Örn: Ev Adresim, İş Adresim"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all duration-300"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <UserIcon className="w-4 h-4 inline mr-1" />
                            Ad Soyad *
                        </label>
                        <input
                            type="text"
                            name="full_name"
                            value={formData.full_name}
                            onChange={handleInputChange}
                            placeholder="Adınız ve soyadınız"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all duration-300"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <PhoneIcon className="w-4 h-4 inline mr-1" />
                            Cep Telefonu *
                        </label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="0555 123 45 67"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all duration-300"
                        />
                    </div>
                </div>

                {/* Konum Bilgileri */}
                <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                        <MapPinIcon className="w-5 h-5 mr-2 text-brand-green" />
                        Konum Bilgileri
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                İl *
                            </label>
                            <select
                                name="city"
                                value={formData.city}
                                onChange={handleProvinceChange}
                                required
                                disabled={loadingProvinces}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all duration-300 bg-white disabled:bg-gray-100"
                            >
                                <option value="">{loadingProvinces ? 'İller yükleniyor...' : 'İl Seçiniz'}</option>
                                {provinces.map(province => (
                                    <option key={province.id} value={province.name}>
                                        {province.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                İlçe *
                            </label>
                            <select
                                name="district"
                                value={formData.district}
                                onChange={handleDistrictChange}
                                required
                                disabled={!formData.city || loadingDistricts}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all duration-300 bg-white disabled:bg-gray-100"
                            >
                                <option value="">{loadingDistricts ? 'İlçeler yükleniyor...' : 'İlçe Seçiniz'}</option>
                                {districts.map(district => (
                                    <option key={district.id} value={district.name}>
                                        {district.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Mahalle
                            </label>
                            <select
                                name="neighborhood"
                                value={formData.neighborhood}
                                onChange={handleInputChange}
                                disabled={!formData.district || loadingNeighborhoods}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all duration-300 bg-white disabled:bg-gray-100"
                            >
                                <option value="">{loadingNeighborhoods ? 'Mahalleler yükleniyor...' : 'Mahalle Seçiniz'}</option>
                                {neighborhoods.map(neighborhood => (
                                    <option key={neighborhood.id} value={neighborhood.name}>
                                        {neighborhood.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Açık Adres */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Açık Adres *
                    </label>
                    <textarea
                        name="address_line"
                        value={formData.address_line}
                        onChange={handleInputChange}
                        placeholder="Sokak, cadde, apartman adı, daire numarası gibi detayları yazınız..."
                        required
                        rows="4"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all duration-300 resize-none"
                    />
                </div>

                {/* Butonlar */}
                <div className="flex flex-col sm:flex-row gap-4 pt-6">
                    <button
                        type="button"
                        onClick={closeModal}
                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-all duration-300"
                    >
                        İptal
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-gradient-to-r from-brand-green to-brand-green-light hover:from-brand-green-light hover:to-brand-green text-white font-bold py-3 px-6 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                        {loading ? (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                Kaydediliyor...
                            </div>
                        ) : (
                            <div className="flex items-center justify-center">
                                <CheckIcon className="w-5 h-5 mr-2" />
                                {addressToEdit ? 'Güncelle' : 'Kaydet'}
                            </div>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default ModernAddressForm;